import React, { useCallback, useEffect, useState } from "react";
import * as Sentry from "@sentry/browser";
import { EventType, libraryForEventType } from "../eventTypes";
import { LoadedDestinationInfo } from "../types/global";
import { makeStyles } from "@material-ui/styles";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  FormControlLabel,
  FormGroup,
  Modal,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import DatetimeRangePicker from "react-datetime-range-picker";
import clsx from "clsx";
import {
  SettingsIcon,
  UploadToCloudIcon,
} from "../components/icons/themes/windows10";
import {
  getTimezoneOffsetString,
  getTimezoneString,
  getUTCOffsetMinutes,
  makeRequest,
} from "../util";
import { useAllSaasDestinations } from "../hooks/useAllSaasDestinations";
import { useDestinationMetadataManager } from "../hooks/useDestinationMetadataManager";
import useInterval from "react-use/lib/useInterval";
import {
  destinationKeyToSlugMap,
  slugForDestination,
  warehouseDestinations,
} from "../destinations/destinations";
import { HIPAAModeTooltip } from "../components/HIPAAModeTooltip";
import { useModalStyles } from "../EventsPage.jsx";
import CloseIcon from "@material-ui/icons/Close";

const useStyles = makeStyles((theme) => ({
  icon: {
    display: "flex",
    marginRight: theme.spacing(1),
  },
  smallIcon: {
    display: "flex",
    marginRight: theme.spacing(1) / 2,
  },
  content: {
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  backfillButton: {
    paddingRight: 10,
    paddingLeft: 10,
  },
  destinationSettingsButton: {
    display: "flex",
    alignItems: "center",
    marginTop: 0,
    "&:hover": {
      cursor: "pointer",
    },
  },
  colorBlue: {
    color: "blue",
  },
  bold: {
    fontWeight: 550,
  },
  noMarginTop: {
    marginTop: 0,
  },
  destinationInfo: {
    display: "flex",
    alignItems: "center",
  },
  destinationName: {
    color: "#444",
    fontSize: 16,
    verticalAlign: "center",
    marginLeft: theme.spacing(2),
  },
  destinationSubtext: {
    color: "#444",
    fontSize: 12,
    verticalAlign: "center",
    marginLeft: theme.spacing(2),
  },
}));

type BackfillStatusType = "IN_PROGRESS" | "QUEUED" | "FINISHED" | "ERROR";

type BackfillStatusInProgress = {
  status: "IN_PROGRESS";
};

type BackfillStatusQueued = {
  status: "QUEUED";
};

type BackfillStatusFinished = {
  status: "FINISHED";
  finishedAt: number;
};

type BackfillStatusError = {
  status: "ERROR";
  createdAt: number;
};

type BackfillStatus = { status: BackfillStatusType } & (
  | BackfillStatusInProgress
  | BackfillStatusQueued
  | BackfillStatusFinished
  | BackfillStatusError
);

function isBackfillInProgress(
  b?: BackfillStatus
): b is BackfillStatusInProgress {
  return b !== undefined && b.status === "IN_PROGRESS";
}

function isBackfillQueued(b?: BackfillStatus): b is BackfillStatusQueued {
  return b !== undefined && b.status === "QUEUED";
}

function isBackfillFinished(b?: BackfillStatus): b is BackfillStatusFinished {
  return b !== undefined && b.status === "FINISHED";
}

function isBackfillError(b?: BackfillStatus): b is BackfillStatusError {
  return b !== undefined && b.status === "ERROR";
}

export type BackfillModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (
    destId: string,
    range: { start: number | null; end: number | null }
  ) => void;
  destination: LoadedDestinationInfo;
};

function BackfillModal({
  open,
  onClose,
  onConfirm,
  destination,
}: BackfillModalProps) {
  const classes = useModalStyles();

  const nullBackfillRange = {
    start: null,
    end: null,
  };

  // backfillRangeTimestamps has start and end as unix timestamps (seconds since epoch)
  const [backfillRangeTimestamps, setBackfillRangeTimestamps] = useState<{
    start: number | null;
    end: number | null;
  }>(nullBackfillRange);

  const [filterBackfillRange, setFilterBackfillRange] = useState<boolean>(
    false
  );
  const [showLocaltimeInDatepicker, setShowLocaltimeInDatepicker] = useState<
    boolean
  >(true);

  if (!open) {
    return null;
  }

  // the DatetimeRangePicker expects a date object which it always interprets in local time
  // when showLocaltimeInDatepicker = false we want to show times in UTC to the user
  // so we need to do the translation of time ourself. lets understand it with an example:
  // consider: user timezone = PST which is UCT-8
  // then utcOffsetMinutes = +8*60 (UTC is 8 hrs ahead of PST)
  // with showLocaltimeInDatepicker = false:
  // if the user had picked 1 AM localtime previously, we'll add 8 hrs to it, to show as 9 AM.
  // if the user picks 10 AM from the datepicker, we need to subtract 8 hrs to correctly interpret the time as 2 AM.

  // translate timestamps for display in the datepicker UI
  let datePickerStartDate: Date | undefined;
  if (backfillRangeTimestamps.start) {
    datePickerStartDate = new Date(
      (showLocaltimeInDatepicker
        ? backfillRangeTimestamps.start
        : backfillRangeTimestamps.start + getUTCOffsetMinutes() * 60) * 1000
    );
  }
  let datePickerEndDate: Date | undefined;
  if (backfillRangeTimestamps.end) {
    datePickerEndDate = new Date(
      (showLocaltimeInDatepicker
        ? backfillRangeTimestamps.end
        : backfillRangeTimestamps.end + getUTCOffsetMinutes() * 60) * 1000
    );
  }

  // interpret back timestamps from user action in datepicker
  function updateDateRange(event) {
    let dateRangeTimestamps: { start: number | null; end: number | null } = {
      start: null,
      end: null,
    };
    if (!isNaN(event.start)) {
      dateRangeTimestamps.start = Math.round(event.start.getTime() / 1000);
      if (!showLocaltimeInDatepicker) {
        dateRangeTimestamps.start -= getUTCOffsetMinutes() * 60;
      }
    }
    if (!isNaN(event.end)) {
      dateRangeTimestamps.end = Math.round(event.end.getTime() / 1000);
      if (!showLocaltimeInDatepicker) {
        dateRangeTimestamps.end -= getUTCOffsetMinutes() * 60;
      }
    }
    setBackfillRangeTimestamps(dateRangeTimestamps);
  }

  let buttonWording = filterBackfillRange
    ? "FILTERED BACKFILL"
    : "COMPLETE BACKFILL";

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title={"Backfill destination: " + destination.name}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography component="p" className={classes.paragraph}>
            This will send historical data matching this event definition into
            the selected destination. You can define bounds for this action if
            you only wish to backfill for a certain period.
          </Typography>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell align="right" width="50%">
                  <Switch
                    checked={filterBackfillRange}
                    color="secondary"
                    name="enable-filtered-backfill"
                    onChange={() => {
                      setFilterBackfillRange(!filterBackfillRange);
                    }}
                    value={filterBackfillRange}
                    disabled={false}
                  />
                </TableCell>

                <TableCell align="left" style={{ fontWeight: 500 }}>
                  Limit the backfill period
                </TableCell>
              </TableRow>
              <TableRow
                style={{ display: filterBackfillRange ? "table-row" : "none" }}
              >
                <TableCell align="center" colSpan={2}>
                  <DatetimeRangePicker
                    timeFormat="hh:mm a"
                    dateFormat="MM/DD"
                    startDate={datePickerStartDate}
                    endDate={datePickerEndDate}
                    inline={true}
                    input={false}
                    onChange={updateDateRange}
                  />
                  <br />
                  <div style={{ textAlign: "left" }}>
                    <p>
                      Both start and end are in{" "}
                      {showLocaltimeInDatepicker ? (
                        <>
                          local timezone{" "}
                          <span className="text-brand-red">
                            {getTimezoneString()}
                          </span>{" "}
                          ({getTimezoneOffsetString()})
                        </>
                      ) : (
                        "UTC"
                      )}
                      .
                    </p>
                    <FormGroup>
                      <FormControlLabel
                        style={{ pointerEvents: "none" }} // prevents the label from being clickable
                        control={
                          <Switch
                            style={{ pointerEvents: "auto" }}
                            checked={showLocaltimeInDatepicker}
                            color="secondary"
                            onChange={() => {
                              setShowLocaltimeInDatepicker(
                                !showLocaltimeInDatepicker
                              );
                            }}
                          />
                        }
                        label="Use local timezone"
                      />
                    </FormGroup>
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button onClick={onClose}>CANCEL</Button>
          <Button
            className={clsx(classes.backfillButton)}
            onClick={() => {
              onConfirm(
                destination.id,
                filterBackfillRange
                  ? backfillRangeTimestamps
                  : nullBackfillRange
              );
              onClose();
            }}
            variant="contained"
          >
            {buttonWording}
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

function BackfillMenuOpenerButton({
  dest,
  destinationDefinition,
  classes,
  disabled,
  handleBackfill,
}) {
  const [backfillModalOpen, setBackfillModalOpen] = useState<boolean>(false);

  const destinationMetadataManager = useDestinationMetadataManager();
  if (
    !destinationMetadataManager.destinationSupportsBackfill(
      slugForDestination(dest)
    ) ||
    !dest.is_valid_server_side
  ) {
    return null;
  }

  if (destinationDefinition.type === "warehouse") {
    return (
      <Tooltip title="Warehouse destinations are automatically backfilled">
        <div>
          <Button
            color="primary"
            variant="outlined"
            disabled
            className={classes.backfillButton}
          >
            <UploadToCloudIcon className={classes.icon} />
            BACKFILL
          </Button>
        </div>
      </Tooltip>
    );
  }

  return (
    <>
      <Button
        color="primary"
        variant="outlined"
        className={classes.backfillButton}
        disabled={disabled}
        onClick={() => setBackfillModalOpen(true)}
      >
        <UploadToCloudIcon className={classes.icon} />
        BACKFILL
      </Button>
      {backfillModalOpen && (
        <BackfillModal
          open={backfillModalOpen}
          onClose={() => setBackfillModalOpen(false)}
          onConfirm={handleBackfill}
          destination={dest}
        />
      )}
    </>
  );
}

export type EventDestinationsProps = {
  eventType: EventType;
  eventDestinationId: string;
  eventIsCustom: boolean;
  destinations: LoadedDestinationInfo[];
  enabledDests: Record<string, boolean>;
  disabledBackfills: Record<string, boolean>;
  onToggleDestination: (destId: string) => void;
  openDestinationsSettingModal: () => void;
  segmentCompatible: boolean;
  handleBackfill: (destId: string, range: unknown) => void;
};

function EventDestinations({
  eventType,
  eventDestinationId,
  eventIsCustom,
  destinations,
  enabledDests,
  disabledBackfills,
  onToggleDestination,
  openDestinationsSettingModal,
  segmentCompatible,
  handleBackfill,
}: EventDestinationsProps) {
  const classes = useStyles();

  const destinationMetadataManager = useDestinationMetadataManager();

  const allSaasDestinations = useAllSaasDestinations();

  // Undefined means waiting for api response, null means errored response
  const [backfillStatuses, setBackfillStatuses] = useState<Record<
    string,
    BackfillStatus
  > | null>(null);

  const initBackfillStatuses = useCallback(() => {
    makeRequest<Record<string, BackfillStatus> | null>(
      "get_backfill_event_statuses",
      {
        event_def_id: eventDestinationId,
      }
    )
      .then((resp) => {
        setBackfillStatuses(resp.data);
      })
      .catch((error) => {
        Sentry.captureException(error);
        setBackfillStatuses(!backfillStatuses ? null : backfillStatuses);
      });
  }, [eventDestinationId]);

  useEffect(() => {
    initBackfillStatuses();
  }, [initBackfillStatuses]);

  // Poll every minute to get backfill statuses
  useInterval(() => {
    initBackfillStatuses();
  }, 1000 * 60);

  const allDestinations = { ...warehouseDestinations, ...allSaasDestinations };
  if (!destinations) {
    return null;
  }

  const library = libraryForEventType(eventType);

  const getBackfillStatusCopy = (destId: string) => {
    if (!backfillStatuses || !backfillStatuses[destId]) {
      return undefined;
    }

    const backfillStatus = backfillStatuses[destId];

    if (isBackfillInProgress(backfillStatus)) {
      return "Backfill in progress. Feel free to leave this page.";
    }

    if (isBackfillQueued(backfillStatus)) {
      return "Starting backfill.";
    }

    if (isBackfillFinished(backfillStatus)) {
      return `Backfill finished on ${new Date(
        backfillStatus.finishedAt
      ).toLocaleDateString()}.`;
    }

    if (isBackfillError(backfillStatus)) {
      return `Something went wrong with backfill started on ${new Date(
        backfillStatus.createdAt
      ).toLocaleDateString()}. Please try again.`;
    }
  };

  const renderedDestinations = destinations
    .filter((dest) => {
      const destinationSlug =
        destinationKeyToSlugMap[dest.name] || dest.function_id;
      const destinationDefinition = allDestinations[destinationSlug];
      return (
        dest.enabled &&
        destinationMetadataManager.destinationIsValidForEvent(
          dest,
          eventType
        ) &&
        (!destinationDefinition.supportedSources ||
          destinationDefinition.supportedSources[library])
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((dest) => {
      const destinationSlug =
        destinationKeyToSlugMap[dest.name] || dest.function_id;
      const destinationDefinition = allDestinations[destinationSlug];
      const DestinationIcon = destinationDefinition.logoSquare;

      return (
        <TableRow key={dest.id}>
          <TableCell>
            <div className={classes.destinationInfo}>
              {DestinationIcon && <DestinationIcon height={34} width={34} />}
              <div>
                <div className={classes.destinationName}>
                  {destinationDefinition.name}
                  {destinationDefinition?.supportsMultiConfig && (
                    <>
                      <br />
                      <small>
                        {destinationDefinition.multiConfigLabel}:{" "}
                        {dest.instance_name}
                      </small>
                    </>
                  )}
                </div>
                <div className={classes.destinationSubtext}>
                  {getBackfillStatusCopy(dest.id)}
                </div>
              </div>
              <HIPAAModeTooltip dest={dest} />
            </div>
          </TableCell>

          <TableCell align="center">
            <Switch
              checked={
                (segmentCompatible &&
                  !destinationMetadataManager.destinationIsWarehouse(
                    slugForDestination(dest)
                  )) ||
                (enabledDests[dest.id] ?? false)
              }
              color="secondary"
              name="enable-destination-for-event"
              onChange={() => onToggleDestination(dest.id)}
              value={segmentCompatible || (enabledDests[dest.id] ?? false)}
              disabled={
                segmentCompatible &&
                !destinationMetadataManager.destinationIsWarehouse(
                  slugForDestination(dest)
                )
              }
            />
          </TableCell>

          <TableCell align="center">
            <BackfillMenuOpenerButton
              dest={dest}
              destinationDefinition={destinationDefinition}
              classes={classes}
              disabled={
                (disabledBackfills[dest.id] ?? false) ||
                backfillStatuses === null ||
                isBackfillInProgress(backfillStatuses[dest.id]) ||
                isBackfillQueued(backfillStatuses[dest.id])
              }
              handleBackfill={(destId, range) => {
                handleBackfill(destId, range);
                setBackfillStatuses({
                  ...backfillStatuses,
                  [destId]: { status: "QUEUED" },
                });
              }}
            />
          </TableCell>
        </TableRow>
      );
    });

  return (
    <Card>
      <CardHeader
        title="Event Destinations"
        classes={{ action: clsx(classes.noMarginTop) }}
        action={
          eventIsCustom && (
            <div
              className={clsx(classes.destinationSettingsButton)}
              onClick={() => openDestinationsSettingModal()}
            >
              <SettingsIcon className={clsx(classes.smallIcon)} />
              <Typography component="h1">
                <span className={clsx(classes.colorBlue, classes.bold)}>
                  SETTINGS
                </span>
              </Typography>
            </div>
          )
        }
      />
      <Divider />
      <CardContent className={classes.content}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Destination</TableCell>
              <TableCell align="center">Active</TableCell>
              <TableCell align="center">Backfill</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderedDestinations}</TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default EventDestinations;
